import React, { ReactNode, useEffect } from 'react'
import { VisuallyHidden } from '@chakra-ui/react'
import Script from 'next/script'
import { gql } from '@urql/core'
import { getEnvGlobal } from 'config'
import { Job } from 'entities/job'
import { Location } from 'entities/location'
import {
    JobForTrackingPixels,
    useTrackingPixels,
} from 'containers/job/tracking-pixels/context/WithTrackingPixels'

const urlTalentRegistration =
    'https://www.talent.com/tracker/img-pixel.php?tracker=functionalworks&apply_step=mainstep_registration'
const urlTalentApplication =
    'https://www.talent.com/tracker/img-pixel.php?tracker=functionalworks&apply_step=step2_application'
const urlZipRecruiter =
    'https://track.ziprecruiter.com/conversion?enc_account_id=06536b20'
const urlAdzunaUK = 'https://www.adzuna.co.uk/app_complete/7616'
const urlAdzunaUSA = 'https://www.adzuna.com/app_complete/7615'
const urlAdzunaGermany = 'https://www.adzuna.de/app_complete/7618'
const urlAdzunaCanada = 'https://www.adzuna.ca/app_complete/8188'
const urlAdzunaNetherlands = 'https://www.adzuna.nl/app_complete/7617'
const urlJooble = 'https://jooble.org/conversion/htmlpixel?clientId=16094'
const urlAppcastRegistration = 'https://click.appcast.io/pixels/generic2-18205.js?ent=196'
const urlAppcastApplication = 'https://click.appcast.io/pixels/generic3-18205.js?ent=196'
const urlAppcastView = 'https://click.appcast.io/pixels/generic1-18205.js?ent=196'

const Tp = ({ id, children }: { id: string; children: ReactNode }) => {
    return <div id={id}>{children}</div>
}
const TalentRegistration = () => (
    <Tp id={'talent-registration'}>
        <img src={urlTalentRegistration} />
    </Tp>
)

const TalentApplication = () => (
    <Tp id={'talent-application'}>
        <img src={urlTalentApplication} />
    </Tp>
)

const ZipRecruiter = () => (
    <Tp id={'zip-recruiter'}>
        <img src={urlZipRecruiter} width="1" height="1" />
    </Tp>
)

const Jooble = () => (
    <Tp id={'jooble'}>
        <img src={urlJooble} width="1" height="1" />
    </Tp>
)

const AppcastRegistration = () => (
    <Tp id={'appcast-registration'}>
        <Script src={urlAppcastRegistration} />
    </Tp>
)

const AppcastApplication = () => (
    <Tp id={'appcast-application'}>
        <Script src={urlAppcastApplication} />
    </Tp>
)

const AppcastView = () => (
    <Tp id={'appcast-view'}>
        <Script src={urlAppcastView} />
    </Tp>
)

function jobToAdzunaInfo(job?: JobForTrackingPixels): [string, string] | undefined {
    const country = job?.location?.country
    if (country) {
        if (country.includes('UK') || country.includes('United Kingdom')) {
            return ['adzuna-uk', urlAdzunaUK]
        } else if (
            country.includes('USA') ||
            country.includes('United States of America') ||
            country.includes('United States')
        ) {
            return ['adzuna-usa', urlAdzunaUSA]
        } else if (country.includes('Netherlands')) {
            return ['adzuna-netherlands', urlAdzunaNetherlands]
        } else if (country.includes('Germany')) {
            return ['adzuna-germany', urlAdzunaGermany]
        } else if (country.includes('Canada')) {
            return ['adzuna-canada', urlAdzunaCanada]
        }
    }
}

const Adzuna = ({ job }: { job?: JobForTrackingPixels }) => {
    const [id, url] = jobToAdzunaInfo(job) || []

    if (!id) {
        return null
    }

    return (
        <Tp id={id}>
            <iframe src={url} scrolling="no" frameBorder="0" width="1" height="1" />
        </Tp>
    )
}

// ------------------------------

const LAST_VISIT_KEY = 'wh_last_visit'

const visitTime = {
    isFirstVisit: () => {
        return !Boolean(localStorage.getItem(LAST_VISIT_KEY))
    },
    setVisitTime: () => {
        localStorage.setItem(LAST_VISIT_KEY, String(new Date().getTime()))
    },
}

// ------------------------------

const TrackingPixelsRegistration = () => {
    const isFirstVisit = visitTime.isFirstVisit()

    useEffect(() => {
        if (isFirstVisit) {
            visitTime.setVisitTime()
        }
    }, [])

    if (!isFirstVisit) {
        return null
    }

    return (
        <>
            <TalentRegistration />
            <AppcastRegistration />
            <ZipRecruiter />
            <Jooble />
        </>
    )
}

const TrackingPixelsApplication = () => {
    return (
        <>
            <TalentApplication />
            <AppcastApplication />
        </>
    )
}

const TrackingPixelsView = ({ job }: { job?: JobForTrackingPixels }) => {
    return (
        <>
            <AppcastView />
            <Adzuna job={job} />
        </>
    )
}

const TrackingPixels = ({ job }: { job?: JobForTrackingPixels }) => {
    if (!getEnvGlobal('shouldShowTrackingPixels')) {
        return null
    }

    const {
        showApplicationTrackingPixels,
        showRegistrationTrackingPixels,
    } = useTrackingPixels()

    return (
        <VisuallyHidden id={'tracking-pixels'}>
            {showRegistrationTrackingPixels && <TrackingPixelsRegistration />}
            {showApplicationTrackingPixels && <TrackingPixelsApplication />}
            <TrackingPixelsView job={job} />
        </VisuallyHidden>
    )
}

export default TrackingPixels
